import { options } from '!/flags'
import { model as i18n } from '!/i18n'
import { register } from '!/repository'
import { model as router } from '!/router'
import { model as bannersModel } from '$/banners'
import { model as moviesFeaturedModel } from '$/movies/featured'
import { model as sessionModel } from '$/session'
import { model as config, modules, remote } from '@/config'
import { noop } from '@/helpers'
import type { TvWithPrice } from '@/interfaces'
import { extendMediaItemByLabelPriceTvChannel } from '@/tools'
import type { BannerUpdateObj } from '@setplex/tria-api'
import { api } from '@setplex/tria-api'
import { combine, createEvent, restore, sample, type Store } from 'effector'
import { and, not, or } from 'patronum'
import { continueWatchingModel } from '~/entities/continue-watching'
import { model as liveEventsModel } from '~/entities/live-events/events'
import {
  model as movieModel,
  model as vodsEntitiesModel,
} from '~/entities/movies'
import { model as movieBundleModel } from '~/entities/movies/bundles'
import { entityPartnerProductRowModel } from '~/entities/partner-product/row'
import { model as tvShowsEntitiesModel } from '~/entities/shows'
import { model as tvShowBundleModel } from '~/entities/shows/bundles'
import { model as tvBundlesModel } from '~/entities/tv/bundles'
import { model as tvFeaturedModel } from '~/entities/tv/featured'
import { model as paymentModel } from '~/features/payment/generic'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import * as pinLockFeature from '~/features/pin-lock'
import { recentlyWatchedModel } from '~/features/recently-watched'
import { model as guest } from '~/features/subscriber/guest'
import { featureSubscriberPartnerProgramModel } from '~/features/subscriber/partner-program'
import '~/shared/ui/translations/general-translations'
import { libraryFavoritesWidget } from '~/widgets/library/favorites'

const DEFAULT_ROW_ELEMENTS_COUNT = 36
const DEFAULT_ROWS_COUNT = 9

// *
// * EVENTS
// *

export const init = createEvent<{
  query: URLSearchParams
}>()

export const left = createEvent()

interface LoadParms {
  page: number
  count?: number
}
export const load = createEvent<LoadParms>()

export const updateBannerItem = createEvent<BannerUpdateObj>()

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

const $recommendedRowsEnabled = config.get(remote.tria_isRecommendedRowsEnabled)

const $liveEventsEnabled = config.get(remote.tria_isLiveEventsEnabled)

// *
// * CONNECTIONS
// *

sample({
  clock: init,
  fn: ({ query }) => ({
    page: Number(query.get('page')) || 0,
    count: Number(query.get('count')) || DEFAULT_ROWS_COUNT,
  }),
  target: load,
})

sample({
  clock: load,
  filter: and(
    not(api.banners.getBannersFx.pending),
    options.get('bannerCarouselsEnabled', true)
  ),
  target: api.banners.getBannersFx,
})

sample({
  clock: load,
  filter: and(
    not(api.liveEvents.initLiveUpcomingEventsFx.pending),
    modules.$liveEventEnabled,
    $liveEventsEnabled
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.liveEvents.initLiveUpcomingEventsFx,
})

sample({
  clock: load,
  filter: and(
    not(api.tvBundles.initBundlesRowByPageFx.pending),
    modules.get('tvEnabled', true)
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.tvBundles.initBundlesRowByPageFx,
})

sample({
  clock: load,
  filter: and(
    not(api.vodBundles.initVodBundlesRowByPageFx.pending),
    modules.get('vodEnabled', true)
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vodBundles.initVodBundlesRowByPageFx,
})

sample({
  clock: load,
  filter: and(
    not(api.showBundles.initShowBundlesRowByPageFx.pending),
    modules.get('tvShowEnabled', true)
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.showBundles.initShowBundlesRowByPageFx,
})

sample({
  clock: load,
  filter: and(
    not(api.vods.getLastAddedMoviesFx.pending),
    modules.get('vodEnabled', true)
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vods.getLastAddedMoviesFx,
})

sample({
  clock: load,
  filter: and(
    not(api.tvshows.getLastAddedTvShowsFx.pending),
    modules.get('tvShowEnabled', true)
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.tvshows.getLastAddedTvShowsFx,
})

sample({
  clock: init,
  filter: and(
    not(api.banners.getFeaturedCarouselFx.pending),
    options.get('featuredCarouselsEnabled', true)
  ),
  fn: noop,
  target: api.banners.getFeaturedCarouselFx,
})

sample({
  clock: load,
  filter: and(
    not(api.vods.loadRealRecommendedVodsFx.pending),
    modules.get('vodEnabled', true),
    $recommendedRowsEnabled
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.vods.loadRealRecommendedVodsFx,
})

sample({
  clock: load,
  filter: and(
    not(api.tv.loadRealRecommendedTvChannelsFx.pending),
    modules.get('tvEnabled', true),
    $recommendedRowsEnabled
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.tv.loadRealRecommendedTvChannelsFx,
})

sample({
  clock: load,
  filter: and(
    not(api.continueWatching.initContinueWatchingRowByPageFx.pending),
    not(guest.$guest),
    sessionModel.$authenticated
  ),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.continueWatching.initContinueWatchingRowByPageFx,
})

sample({
  clock: load,
  filter: and(
    not(entityPartnerProductRowModel.$partnerProductRowLoading),
    featureSubscriberPartnerProgramModel.$partnersProgramEnabled
  ),
  fn: (loadData) => ({ ...loadData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: entityPartnerProductRowModel.loadPartnerProductRow,
})

/********** Skeleton logics starts ***********/
// TODO: perhaps improve the skeleton: reset skeleton when banners store filled or trying to fill if fx fail
const $isAnyInitFxPending = or(
  api.banners.getBannersFx.pending,
  api.banners.getFeaturedCarouselFx.pending,
  api.tvBundles.initBundlesRowByPageFx.pending,
  api.showBundles.initShowBundlesRowByPageFx.pending,
  api.vodBundles.initVodBundlesRowByPageFx.pending,
  api.liveEvents.initLiveUpcomingEventsFx.pending,
  api.vods.getRealRecommendedVodsFx.pending,
  api.tv.getRealRecommendedTvChannelsFx.pending,
  api.continueWatching.initContinueWatchingRowByPageFx.pending,
  api.vods.getLastAddedMoviesFx.pending,
  api.tvshows.getLastAddedTvShowsFx.pending,
  entityPartnerProductRowModel.$partnerProductRowLoading
)

sample({
  clock: $isAnyInitFxPending,
  source: { pathname: router.$pathname },
  filter: ({ pathname }) => pathname === '/',
  fn: (_, isAnyInitFxPending) => isAnyInitFxPending,
  target: setContentLoading,
})
/********** Skeleton logics ends ***********/

// *
// * STORES
// *
export const $lastAddedMovies = combine(
  vodsEntitiesModel.$lastAddedMoviesFormatted,
  api.vods.$pageInfoLastAddedMovies,
  i18n.$t,
  (movies, pageInfo, { t }) => {
    return {
      content: movies,
      name: t('movies_last_added'),
      isMoreThanOnePage: !pageInfo?.last,
    }
  }
)

const $lastAddedMoviesShown = and(
  modules.$vodEnabled,
  $lastAddedMovies.map((vods) => vods.content?.length > 0)
)

export const $lastAddedTvShows = combine(
  tvShowsEntitiesModel.$lastAddedTvShowsFormatted,
  api.tvshows.$lastAddedTvShowsPageInfo,
  i18n.$t,
  (tvShows, pageInfo, { t }) => {
    return {
      content: tvShows,
      name: t('series_last_added'),
      isMoreThanOnePage: !pageInfo?.last,
    }
  }
)
const $lastAddedTvShowsShown = and(
  modules.$tvShowEnabled,
  $lastAddedTvShows.map((tvShows) => tvShows.content?.length > 0)
)

//*
//* BANNERS STORES
//*

export const $featuredCarouselTypeVod = api.banners.$featuredCarouselTypeVod

export const $featuredCarousel = api.banners.$featuredCarousel

const $realRecommendedTvChannels: Store<TvWithPrice[]> = combine(
  api.tv.$realRecommendedTvChannels,
  ssePurchasesModel.$channel,
  i18n.$t,
  (content, adjust) =>
    content
      .map((channel) => extendMediaItemByLabelPriceTvChannel(adjust(channel)))
      .slice(0, DEFAULT_ROW_ELEMENTS_COUNT)
)

const $isContinueWatchingLocked = combine(
  pinLockFeature.$isLockedFn,
  (isLocked) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ contentType, tvShowId, contentId, locked }: any) =>
      isLocked({ id: contentType === 'EPISODE' ? tvShowId : contentId, locked })
)

const $bannerCarouselsShown = and(
  options.$bannerCarouselsEnabled,
  bannersModel.$bannersFilledFormatted.map((banners) => banners?.length > 0)
)

const $featuredVodsCarouselShown = and(
  modules.$vodEnabled,
  options.$featuredCarouselsEnabled,
  moviesFeaturedModel.$featuredCarouselTypeVodFormatted.map((carousels) =>
    carousels?.some(({ content }) => content?.length)
  )
)

const $featuredTvShowsCarouselShown = and(
  modules.$tvShowEnabled,
  api.banners.$featuredCarouselTypeTvShow.map(
    (carousels) => Boolean(carousels?.length) // check is added because content can be null
  )
)

const $vodBundlesShown = and(
  modules.$vodEnabled,
  api.vodBundles.$vodBundlesRow.map((vodBundles) => vodBundles?.length > 0)
)

const $showsBundlesShown = and(
  modules.$tvShowEnabled,
  tvShowBundleModel.$showBundlesRowFormatted.map(
    (showBundles) => showBundles.length > 0
  )
)

const $tvBundlesShown = and(
  modules.$tvEnabled,
  tvBundlesModel.$tvBundlesRowFormatted.map((content) => content.length > 0)
)

const $recommendedVodsShown = and(
  $recommendedRowsEnabled,
  modules.$vodEnabled,
  movieModel.$formattedRecommendedVods.map((content) => content?.length > 0)
)

const $recommendedTvChannelsShown = and(
  modules.$tvEnabled,
  $realRecommendedTvChannels.map((content) => content?.length > 0)
)
// TODO: this store works correctly, but in Skin not correctly (even if reset api.banners.$featuredCarousel here). Need more investigate
const $featuredTvCarouselShown = and(
  modules.$tvEnabled,
  options.$featuredCarouselsEnabled,
  tvFeaturedModel.$featuredCarouselTypeTv.map((carousels) =>
    Boolean(carousels?.length)
  )
)

const $continueWatchingShown = and(
  sessionModel.$authenticated,
  not(guest.$guest),
  continueWatchingModel.$continueWatching.map(
    (continueWatching) => continueWatching.length > 0
  )
)

const $recentlyWatchedShown = and(
  sessionModel.$authenticated,
  not(guest.$guest),
  recentlyWatchedModel.$localRecentlyWatched.map(
    (recentlyWatched) => recentlyWatched.length > 0
  )
)

export const $vodBundlesRowTitle: Store<string> = combine(i18n.$t, ({ t }) =>
  t('movies_bundle')
)
export const $showBundlesRowTitle: Store<string> = combine(i18n.$t, ({ t }) =>
  t('series_bundle')
)
export const $tvBundlesRowTitle: Store<string> = combine(i18n.$t, ({ t }) =>
  t('channel_bundle')
)

export const $continueWatchingTitle: Store<string> = combine(i18n.$t, ({ t }) =>
  t('continue_watching')
)

export const $favoriteVodsRowTitle: Store<string> = combine(i18n.$t, ({ t }) =>
  t('movies_favorite')
)

export const $favoriteTvChannelsRowTitle: Store<string> = combine(
  i18n.$t,
  ({ t }) => t('channel_favorites')
)

export const $favoriteTvShowsRowTitle: Store<string> = combine(
  i18n.$t,
  ({ t }) => t('series_favorite')
)

export const $realRecommendedTvTitle: Store<string> = combine(
  i18n.$t,
  ({ t }) => t('live_channels_may_like')
)
export const $partnerProductRowTitle: Store<string> = combine(
  i18n.$t,
  ({ t }) => t('partner_products')
)

// clear paginatable stores
sample({
  clock: left,
  source: libraryFavoritesWidget.$isAnyFavorites,
  filter: (isAnyFavorites) => isAnyFavorites,
  target: libraryFavoritesWidget.resetFavorites,
})
sample({
  clock: left,
  filter: featureSubscriberPartnerProgramModel.$partnersProgramEnabled,
  target: entityPartnerProductRowModel.resetPartnerProductRow,
})

api.banners.$banners.reset(init)
api.vodBundles.$vodBundlesRow.reset(init) // not necessary
api.showBundles.$showBundlesRow.reset(init) // not necessary
api.tvBundles.$tvBundlesRow.reset(init) // not necessary
api.liveEvents.$liveUpcominEvents.reset(init)
api.vods.$lastAddedMovies.reset(init)
api.tvshows.$lastAddedTvShows.reset(init)

register(movieModel.$formattedRecommendedVods, '$realRecommendedVods')
register(movieModel.$recommendedVodsTitle, '$recommendedVodsTitle')
register($recommendedVodsShown, '$recommendedVodsShown')
register($realRecommendedTvChannels, '$realRecommendedTvChannels')
register($realRecommendedTvTitle, '$realRecommendedTvTitle')
register($recommendedTvChannelsShown, '$recommendedTvChannelsShown')
register(bannersModel.$bannersFilledFormatted, '$bannersFilled')
register($bannerCarouselsShown, '$bannerCarouselsShown')
register(
  moviesFeaturedModel.$featuredCarouselTypeVodFormatted,
  '$vodFeaturedCarousel'
) // names not equal
register(
  moviesFeaturedModel.$featuredCarouselVodTitle,
  '$featuredCarouselVodTitle'
)
register($featuredVodsCarouselShown, '$featuredVodsCarouselShown')
register(tvFeaturedModel.$featuredCarouselTypeTv, '$featuredCarouselTypeTv')
register(tvFeaturedModel.$featuredCarouselTvTitle, '$featuredCarouselTvTitle')
register($featuredTvCarouselShown, '$featuredTvCarouselShown')
register($lastAddedMovies, '$lastAddedMovies')
register($lastAddedMoviesShown, '$lastAddedMoviesShown')
register($lastAddedTvShows, '$lastAddedTvShows')
register($lastAddedTvShowsShown, '$lastAddedTvShowsShown')
register(updateBannerItem, 'updateBannerItem')

register($featuredTvShowsCarouselShown, '$featuredTvShowsCarouselShown')
register(
  liveEventsModel.$liveUpcomingEventsCategory,
  '$liveUpcomingEventsCategory'
)
register(tvBundlesModel.$tvBundlesRowFormatted, '$tvBundlesRow')
register($tvBundlesRowTitle, '$home/root/tvBundlesRowTitle')
register($favoriteTvChannelsRowTitle, '$home/root/favoriteTvChannelsRowTitle')
register($tvBundlesShown, '$tvBundlesShown')
register(movieBundleModel.$vodBundlesRowFormatted, '$vodBundlesRow')
register($vodBundlesRowTitle, '$home/root/vodBundlesRowTitle')
register($favoriteVodsRowTitle, '$home/root/favoriteVodsRowTitle')
register($vodBundlesShown, '$vodBundlesShown')
register(tvShowBundleModel.$showBundlesRowFormatted, '$showBundlesRow')
register($showBundlesRowTitle, '$home/root/showBundlesRowTitle')
register($favoriteTvShowsRowTitle, '$home/root/favoriteTvShowsRowTitle')
register($showsBundlesShown, '$showsBundlesShown')
register(paymentModel.selectMoviePaymentButton, 'selectMoviePaymentButton')
register(paymentModel.selectTvShowPaymentButton, 'selectTvShowPaymentButton')
register($continueWatchingTitle, '$continueWatchingTitle')
register($continueWatchingShown, '$continueWatchingShown')
register(continueWatchingModel.$continueWatching, '$continueWatching')
register(recentlyWatchedModel.$recentlyWatchedFormat, '$recentlyWatched')
register(recentlyWatchedModel.$recentlyWatchedTitle, '$recentlyWatchedTitle')
register($recentlyWatchedShown, '$recentlyWatchedShown')
register($contentLoading, '$homeContentLoading')
register($isContinueWatchingLocked, '|isContinueWatchingLocked')
register(libraryFavoritesWidget.$favoritesContent, '$favoritesContent')
register(
  libraryFavoritesWidget.seeAllLibraryFavoritesMovieClick,
  'seeAllLibraryFavoritesMovieClick'
)
register(
  libraryFavoritesWidget.seeAllLibraryFavoritesTvClick,
  'seeAllLibraryFavoritesTvClick'
)
register(
  libraryFavoritesWidget.seeAllLibraryFavoritesTvShowsClick,
  'seeAllLibraryFavoritesTvShowsClick'
)
register(guest.$guest, '$guest')
register(
  entityPartnerProductRowModel.$partnerProductRowFormatted,
  '$home/partnerProgram/partnerProductRowFormatted'
)
register($partnerProductRowTitle, '$partnerProductRowTitle')
